<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Icon Component
      p.text--secondary A versatile icon component based on Vuetify's VIcon with YZ color palette support.

    // Icon Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in iconVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          // Grouped icons (side by side)
          template(v-if="variation.isGroup")
            .variation-preview.mb-4.pa-4.border.rounded
              .d-flex.flex-wrap.gap-4.align-center
                div(v-for="icon in variation.icons" :key="icon.label" class="d-flex flex-column align-center")
                  yz-icon(v-bind="icon.props") {{ icon.iconName }}
                  small.text--secondary.mt-2(v-if="icon.hint") {{ icon.hint }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                div(v-for="icon in variation.icons" :key="icon.label" class="mb-3")
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === icon.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === icon.code ? 'success' : ''" @click="copyCode(icon.code)")
                    span.font-weight-medium.ml-2 {{ icon.label }}
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ icon.code }}

          // Individual icons
          template(v-else)
            .variation-preview.mb-4.pa-4.border.rounded
              yz-icon(v-bind="variation.props") {{ variation.iconName }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
                span.font-weight-medium.ml-2 Code

              pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in iconProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      iconVariations: [
        {
          name: 'Basic Icons',
          id: 'basic-icons',
          description: 'Standard icons with default styling',
          isGroup: true,
          icons: [
            {
              label: 'Default',
              iconName: 'mdi-home',
              props: {},
              code: 'yz-icon mdi-home',
            },
            {
              label: 'Settings',
              iconName: 'mdi-cog',
              props: {},
              code: 'yz-icon mdi-cog'
            },
            {
              label: 'User',
              iconName: 'mdi-account',
              props: {},
              code: 'yz-icon mdi-account'
            },
            {
              label: 'Search',
              iconName: 'mdi-magnify',
              props: {},
              code: 'yz-icon mdi-magnify'
            }
          ]
        },
        {
          name: 'Icon Sizes',
          id: 'icon-sizes',
          description: 'Different icon sizes using size modifiers and custom sizes',
          isGroup: true,
          icons: [
            {
              label: 'Extra Small',
              iconName: 'mdi-star',
              props: {
                xSmall: true
              },
              code: 'yz-icon(x-small) mdi-star',
              hint: '12px'
            },
            {
              label: 'Small',
              iconName: 'mdi-star',
              props: {
                small: true
              },
              code: 'yz-icon(small) mdi-star',
              hint: '16px'
            },
            {
              label: 'Default',
              iconName: 'mdi-star',
              props: {},
              code: 'yz-icon mdi-star',
              hint: '24px'
            },
            {
              label: 'Large',
              iconName: 'mdi-star',
              props: {
                large: true
              },
              code: 'yz-icon(large) mdi-star',
              hint: '36px'
            },
            {
              label: 'Extra Large',
              iconName: 'mdi-star',
              props: {
                xLarge: true
              },
              code: 'yz-icon(x-large) mdi-star',
              hint: '40px'
            },
            {
              label: 'Custom 32px',
              iconName: 'mdi-star',
              props: {
                size: '32'
              },
              code: 'yz-icon(size="32") mdi-star',
              hint: 'Custom size'
            }
          ]
        },
        {
          name: 'YZ Colors - Semantic',
          id: 'yz-colors-semantic',
          description: 'Icons using YZ semantic color palette',
          isGroup: true,
          icons: [
            {
              label: 'Success',
              iconName: 'mdi-check-circle',
              props: {
                color: 'yz-success'
              },
              code: 'yz-icon(color="yz-success") mdi-check-circle'
            },
            {
              label: 'Warning',
              iconName: 'mdi-alert-circle',
              props: {
                color: 'yz-warning'
              },
              code: 'yz-icon(color="yz-warning") mdi-alert-circle'
            },
            {
              label: 'Danger',
              iconName: 'mdi-close-circle',
              props: {
                color: 'yz-danger'
              },
              code: 'yz-icon(color="yz-danger") mdi-close-circle'
            },
            {
              label: 'Success 500',
              iconName: 'mdi-check',
              props: {
                color: 'yz-success-500'
              },
              code: 'yz-icon(color="yz-success-500") mdi-check'
            },
            {
              label: 'Success 700',
              iconName: 'mdi-check-bold',
              props: {
                color: 'yz-success-700'
              },
              code: 'yz-icon(color="yz-success-700") mdi-check-bold'
            }
          ]
        },
        {
          name: 'YZ Colors - Neutral',
          id: 'yz-colors-neutral',
          description: 'Icons using YZ neutral color palette',
          isGroup: true,
          icons: [
            {
              label: 'Neutral',
              iconName: 'mdi-information',
              props: {
                color: 'yz-neutral'
              },
              code: 'yz-icon(color="yz-neutral") mdi-information'
            },
            {
              label: 'Neutral 400',
              iconName: 'mdi-help-circle',
              props: {
                color: 'yz-neutral-400'
              },
              code: 'yz-icon(color="yz-neutral-400") mdi-help-circle'
            },
            {
              label: 'Neutral 500',
              iconName: 'mdi-dots-horizontal',
              props: {
                color: 'yz-neutral-500'
              },
              code: 'yz-icon(color="yz-neutral-500") mdi-dots-horizontal'
            },
            {
              label: 'Neutral 600',
              iconName: 'mdi-menu',
              props: {
                color: 'yz-neutral-600'
              },
              code: 'yz-icon(color="yz-neutral-600") mdi-menu'
            },
            {
              label: 'Neutral 700',
              iconName: 'mdi-filter',
              props: {
                color: 'yz-neutral-700'
              },
              code: 'yz-icon(color="yz-neutral-700") mdi-filter'
            }
          ]
        },
        {
          name: 'YZ Colors - Brand',
          id: 'yz-colors-brand',
          description: 'Icons using YZ brand color palette',
          isGroup: true,
          icons: [
            {
              label: 'Blue',
              iconName: 'mdi-link',
              props: {
                color: 'yz-blue'
              },
              code: 'yz-icon(color="yz-blue") mdi-link'
            },
            {
              label: 'Blue 500',
              iconName: 'mdi-open-in-new',
              props: {
                color: 'yz-blue-500'
              },
              code: 'yz-icon(color="yz-blue-500") mdi-open-in-new'
            },
            {
              label: 'Green',
              iconName: 'mdi-leaf',
              props: {
                color: 'yz-green'
              },
              code: 'yz-icon(color="yz-green") mdi-leaf'
            },
            {
              label: 'Green 500',
              iconName: 'mdi-nature',
              props: {
                color: 'yz-green-500'
              },
              code: 'yz-icon(color="yz-green-500") mdi-nature'
            },
            {
              label: 'Red',
              iconName: 'mdi-heart',
              props: {
                color: 'yz-red'
              },
              code: 'yz-icon(color="yz-red") mdi-heart'
            },
            {
              label: 'Yellow',
              iconName: 'mdi-star',
              props: {
                color: 'yz-yellow'
              },
              code: 'yz-icon(color="yz-yellow") mdi-star'
            }
          ]
        },
        {
          name: 'Combined Size and Color',
          id: 'combined-size-color',
          description: 'Icons with both size and color properties',
          isGroup: true,
          icons: [
            {
              label: 'Small Success',
              iconName: 'mdi-check',
              props: {
                small: true,
                color: 'yz-success'
              },
              code: 'yz-icon(small color="yz-success") mdi-check'
            },
            {
              label: 'Large Warning',
              iconName: 'mdi-alert-triangle',
              props: {
                large: true,
                color: 'yz-warning-500'
              },
              code: 'yz-icon(large color="yz-warning-500") mdi-alert-triangle'
            },
            {
              label: 'Custom Blue',
              iconName: 'mdi-account-circle',
              props: {
                size: '28',
                color: 'yz-blue-500'
              },
              code: 'yz-icon(size="28" color="yz-blue-500") mdi-account-circle'
            },
            {
              label: 'XL Danger',
              iconName: 'mdi-close-circle-outline',
              props: {
                xLarge: true,
                color: 'yz-danger-500'
              },
              code: 'yz-icon(x-large color="yz-danger-500") mdi-close-circle-outline'
            }
          ]
        },
        {
          name: 'Different Icon Types',
          id: 'icon-types',
          description: 'Different types of icons supported by the component',
          isGroup: true,
          icons: [
            {
              label: 'Material Design',
              iconName: 'mdi-home',
              props: {
                color: 'yz-neutral-700'
              },
              code: 'yz-icon(color="yz-neutral-700") mdi-home'
            },
            {
              label: 'Font Awesome',
              iconName: 'fas fa-user',
              props: {
                color: 'yz-blue-500'
              },
              code: 'yz-icon(color="yz-blue-500") fas fa-user'
            },
            {
              label: 'Material Outlined',
              iconName: 'mdi-heart-outline',
              props: {
                color: 'yz-red-500'
              },
              code: 'yz-icon(color="yz-red-500") mdi-heart-outline'
            }
          ]
        },
        {
          name: 'Custom YZI Icons',
          id: 'custom-yzi-icons',
          description: 'Yezza custom icon set using $yzi- prefix for brand-specific icons',
          isGroup: true,
          icons: [
            {
              label: 'User',
              iconName: '$yzi-user-01',
              props: {
                color: 'yz-neutral-700'
              },
              code: 'yz-icon(color="yz-neutral-700") $yzi-user-01'
            },
            {
              label: 'Users',
              iconName: '$yzi-users-02',
              props: {
                color: 'yz-blue-500'
              },
              code: 'yz-icon(color="yz-blue-500") $yzi-users-02'
            },
            {
              label: 'Calendar',
              iconName: '$yzi-calendar',
              props: {
                color: 'yz-success-500'
              },
              code: 'yz-icon(color="yz-success-500") $yzi-calendar'
            },
            {
              label: 'Check Circle',
              iconName: '$yzi-check-circle',
              props: {
                color: 'yz-success'
              },
              code: 'yz-icon(color="yz-success") $yzi-check-circle'
            },
            {
              label: 'Alert Circle',
              iconName: '$yzi-alert-circle-solid',
              props: {
                color: 'yz-warning'
              },
              code: 'yz-icon(color="yz-warning") $yzi-alert-circle-solid'
            },
            {
              label: 'Edit',
              iconName: '$yzi-edit-02',
              props: {
                color: 'yz-neutral-600'
              },
              code: 'yz-icon(color="yz-neutral-600") $yzi-edit-02'
            },
            {
              label: 'Search',
              iconName: '$yzi-search-sm',
              props: {
                color: 'yz-neutral-500'
              },
              code: 'yz-icon(color="yz-neutral-500") $yzi-search-sm'
            },
            {
              label: 'Upload',
              iconName: '$yzi-upload-01',
              props: {
                color: 'yz-blue-500'
              },
              code: 'yz-icon(color="yz-blue-500") $yzi-upload-01'
            },
            {
              label: 'Download',
              iconName: '$yzi-download-01',
              props: {
                color: 'yz-green-500'
              },
              code: 'yz-icon(color="yz-green-500") $yzi-download-01'
            },
            {
              label: 'Chevron Down',
              iconName: '$yzi-chevron-down',
              props: {
                size: '16',
                color: 'yz-neutral-400'
              },
              code: 'yz-icon(size="16" color="yz-neutral-400") $yzi-chevron-down'
            },
            {
              label: 'Menu',
              iconName: '$yzi-menu-duocolor-01',
              props: {
                color: 'yz-neutral-700'
              },
              code: 'yz-icon(color="yz-neutral-700") $yzi-menu-duocolor-01'
            },
            {
              label: 'Bell',
              iconName: '$yzi-bell-01',
              props: {
                color: 'yz-warning-500'
              },
              code: 'yz-icon(color="yz-warning-500") $yzi-bell-01'
            },
            {
              label: 'Package',
              iconName: '$yzi-package',
              props: {
                color: 'yz-blue-500'
              },
              code: 'yz-icon(color="yz-blue-500") $yzi-package'
            },
            {
              label: 'Medical Square',
              iconName: '$yzi-medical-square',
              props: {
                color: 'yz-success-500'
              },
              code: 'yz-icon(color="yz-success-500") $yzi-medical-square'
            },
            {
              label: 'Yezza Icon',
              iconName: '$yzi-yezza-icon',
              props: {
                color: 'yz-neutral-700',
                size: '32'
              },
              code: 'yz-icon(size="32" color="yz-neutral-700") $yzi-yezza-icon'
            }
          ]
        }
      ],
      iconProps: [
        {
          name: 'size',
          type: 'Number | String',
          default: '24px',
          description: 'Custom size in pixels or CSS units'
        },
        {
          name: 'xSmall',
          type: 'Boolean',
          default: 'false',
          description: 'Sets icon size to 12px'
        },
        {
          name: 'small',
          type: 'Boolean',
          default: 'false',
          description: 'Sets icon size to 16px'
        },
        {
          name: 'large',
          type: 'Boolean',
          default: 'false',
          description: 'Sets icon size to 36px'
        },
        {
          name: 'xLarge',
          type: 'Boolean',
          default: 'false',
          description: 'Sets icon size to 40px'
        },
        {
          name: 'color',
          type: 'String',
          default: 'inherit',
          description: 'Icon color using YZ color palette (e.g., yz-success, yz-neutral-500)'
        },
        {
          name: 'dense',
          type: 'Boolean',
          default: 'false',
          description: 'Reduces the space around the icon'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Applies disabled styling to the icon'
        },
        {
          name: 'left',
          type: 'Boolean',
          default: 'false',
          description: 'Applies left margin for spacing'
        },
        {
          name: 'right',
          type: 'Boolean',
          default: 'false',
          description: 'Applies right margin for spacing'
        },
        {
          name: 'tag',
          type: 'String',
          default: 'i',
          description: 'HTML tag to render the icon as'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>